<!--
  src/components/DialogAssignTask.vue
  Stage 2 — place a task that is ALREADY LINKED to the course (a membership) into a plan on a date.
  The picker searches the course's memberships (getCourseTaskMemberships) and its value is the
  membership id (tasksBankCourseId), which is what the plan is placed with — NOT a bank task id.
  Opened as a Buefy modal (hasModalCard) from the plan tab's button and on row-click (sideViewModal).
  Course is implicit (store.state.auth.course.id).
-->
<template>
  <div class="DialogAssignTask">
    <div class="modal-card" style="width: 460px;">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ isEdit ? "עריכת שיבוץ משימה" : "שיבוץ משימה בתכנית" }}</p>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <section class="modal-card-body">
        <div class="course-context">
          שיבוץ בקורס הנוכחי: <strong>{{ courseName }}</strong>
        </div>

        <b-field label="משימה משויכת לקורס">
          <FieldInlineSelect
            label="בחירת משימה"
            placeholder="הקלד שם / מזהה משימה מהמשימות המשויכות לקורס"
            :alert-not-exist="true"
            :hide-hover="true"
            :clearable="true"
            :filterable="true"
            :read-only="isEdit"
            :options="[]"
            :fetch-options="onSearchTasks"
            :initial-option="taskInitialOption"
            v-model="model.id"
            @onChange="onChange" />
        </b-field>

        <b-field label="סוג תוכנית">
          <b-select v-model="model.programTypeId" placeholder="בחר סוג תוכנית" expanded :disabled="isEdit">
            <option v-for="p in programTypes" :key="p.Value" :value="p.Value">{{ p.Text }}</option>
          </b-select>
        </b-field>

        <b-field label="תאריך">
          <b-datepicker v-model="model.date" icon="calendar-today"
            placeholder="בחר תאריך" trap-focus editable />
        </b-field>

        <b-field label="חלון זמן (TimeWindow)">
          <div class="time-window">
            <button v-for="w in timeWindows" :key="w.Value" type="button"
              :class="{ on: model.timeWindow === w.Value }"
              :style="model.timeWindow === w.Value ? { background: w.color, borderColor: w.color, color: '#fff' } : {}"
              @click="model.timeWindow = w.Value">
              <span class="dot" :style="{ background: w.color }"></span>{{ w.Text }}
            </button>
          </div>
        </b-field>

        <div class="times">
          <b-field label="שעת התחלה">
            <b-timepicker v-model="model.startTime" hour-format="24" icon="clock" placeholder="08:00" />
          </b-field>
          <b-field label="שעת סיום">
            <b-timepicker v-model="model.endTime" hour-format="24" icon="clock" placeholder="12:00" />
          </b-field>
        </div>
        <!-- Per-program duration. Empty = inherit the shared bank's value; anything else
             pins this placement's own duration for this program only. -->
        <b-field label="משך המשימה (דקות)">
          <div class="duration">
            <b-input type="number" min="1" step="1" v-model="model.durationMinutes"
              :placeholder="durationPlaceholder" expanded />
            <span v-if="isDurationOverridden" class="duration-badge">מותאם לתוכנית</span>
          </div>
        </b-field>
        <p class="duration-hint">{{ durationHint }}</p>

        <!-- SortOrder is assigned automatically per-day by the server; reorder via the list arrows. -->
      </section>

      <footer class="modal-card-foot">
        <b-button :loading="isLoading" type="is-primary" :label="isEdit ? 'שמור' : 'שייך משימה'" @click="save" />
        <b-button :disabled="isLoading" label="ביטול" @click="$emit('close')" />
      </footer>
    </div>
  </div>
</template>

<script>
import { ToastProgrammatic as Toast } from "buefy";
import FieldInlineSelect from "@/components/Fields/FieldInlineSelect.vue";
import TasksService from "@/services/TasksService";
import moment from "moment";

const TIME_WINDOWS = [
  { Value: "any", Text: "כל היום", color: "#00427e" },
  { Value: "morning", Text: "בוקר", color: "#00afd8" },
  { Value: "evening", Text: "ערב", color: "#E1583D" },
];

export default {
  name: "DialogAssignTask",
  components: { FieldInlineSelect },
  // row + onSuccess arrive from EntityListLayout.openModal (sideViewModal); store always passed.
  props: ["store", "row", "defaultDate", "defaultProgramTypeId", "onSuccess"],
  data() {
    return {
      isLoading: false,
      programTypes: [],
      timeWindows: TIME_WINDOWS,
      taskInitialOption: null,   // seeds the picker label in edit mode
      model: {
        id: null,
        programTypeId: this.defaultProgramTypeId != null ? this.defaultProgramTypeId : null,
        date: this.defaultDate ? new Date(this.defaultDate) : new Date(),
        timeWindow: "any",
        startTime: null,
        endTime: null,
        // The OVERRIDE, as a string from the input. "" = inherit the bank.
        durationMinutes: "",
      },
      // Bank duration of the placed row, for the placeholder/hint in edit mode.
      bankDurationMinutes: null,
    };
  },
  computed: {
    courseId() {
      return this.store.state.auth.course.id;
    },
    courseName() {
      return this.store.state.auth.course.name;
    },
    isEdit() {
      return !!(this.row && this.row.id);
    },
    // Reflects what's typed now, not what was saved — so the badge appears the moment
    // the admin pins a value and disappears when they clear the field.
    isDurationOverridden() {
      return String(this.model.durationMinutes || "").trim() !== "";
    },
    // Empty input === inherit, so the bank's duration is the hint. Only known when
    // editing a placed row; on add the server resolves it from the membership.
    durationPlaceholder() {
      if (this.bankDurationMinutes != null) return `${this.bankDurationMinutes} (מבנק המשימות)`;
      return "ריק = משך מבנק המשימות";
    },
    durationHint() {
      if (!this.isDurationOverridden) {
        return "השדה ריק — המשימה משתמשת במשך מבנק המשימות, ותתעדכן אוטומטית אם המשך בבנק ישתנה.";
      }
      return "משך ייחודי לתוכנית זו — שינוי המשך בבנק המשימות לא ישפיע על שיבוץ זה. נקה את השדה כדי לחזור למשך מהבנק.";
    },
  },
  created() {
    TasksService.getProgramTypes().then((r) => {
      this.programTypes = (r.data || []).map((i) => ({ Value: i.id, Text: i.name }));
    });

    if (this.isEdit) {
      const r = this.row;
      // Seed the picker label from the placed row (task can't be changed on edit).
      this.taskInitialOption = { Value: r.tasksBankCourseId, Text: `${r.tasksBankId} - ${r.taskName}` };
      this.bankDurationMinutes = r.bankDurationMinutes != null ? r.bankDurationMinutes : null;
      this.model = this.toModel(r);
    }
  },
  methods: {
    // Map a StructuredPlanTask row (from the list/calendar) into the form model.
    toModel(r) {
      return {
        id: r.tasksBankCourseId,   // membership id (picker value); task is fixed on edit
        programTypeId: r.programTypeId,
        date: r.taskDate ? new Date(r.taskDate) : new Date(),
        timeWindow: r.timeWindow,
        startTime: this.toDate(r.startTime),
        endTime: this.toDate(r.endTime),
        // Bind to the OVERRIDE, never to the resolved durationMinutes — seeding the input
        // with the resolved value would turn an inherited duration into a pinned one on save.
        durationMinutes: r.durationMinutesOverride != null ? String(r.durationMinutesOverride) : "",
      };
    },
    // Schedule fields shared by add (per Task item) and update (per Tasks[] row).
    // Format the date as local YYYY-MM-DD so JSON's UTC conversion can't shift the day back.
    // DurationMinutes is always sent: on update, omitting it would CLEAR an existing override,
    // and null is the correct way to say "inherit the bank".
    scheduleFields() {
      return {
        TaskDate: this.model.date ? moment(this.model.date).format("YYYY-MM-DD") : null,
        StartTime: this.fmtTime(this.model.startTime),
        EndTime: this.fmtTime(this.model.endTime),
        TimeWindow: this.model.timeWindow,
        DurationMinutes: this.durationValue(),
      };
    },
    // "" -> null (inherit); otherwise the number. Validity is checked in save().
    durationValue() {
      const raw = String(this.model.durationMinutes == null ? "" : this.model.durationMinutes).trim();
      return raw === "" ? null : Number(raw);
    },
    // Edit — one row of the update batch. SortOrder is required and not preserved server-side,
    // so echo the row's current value back: this dialog only edits the schedule, never the order.
    buildUpdateRow() {
      return {
        Id: this.row.id,
        SortOrder: this.row.sortOrder,
        ...this.scheduleFields(),
      };
    },
    // Add — batch body (AddStructuredPlanTasksModel) with a single task. SortOrder is auto-assigned per day.
    buildAddDto() {
      return {
        CourseId: this.courseId,
        ProgramTypeId: this.model.programTypeId,
        Tasks: [{ TasksBankCourseId: this.model.id, ...this.scheduleFields() }],
      };
    },
    toDate(hhmm) {
      if (!hhmm) return null;
      const [h, m] = hhmm.split(":");
      const d = new Date();
      d.setHours(+h, +m, 0, 0);
      return d;
    },
    fmtTime(d) {
      if (!d) return null;
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    onChange() {},
    // Placement (stage 2) sources from the COURSE's linked tasks (memberships), NOT the bank.
    // The picker value is the membership id (tasksBankCourseId), which is what the plan is placed with.
    onSearchTasks({ search, loading, setData, setValue }) {
      if (search.length > 1) {
        loading(true);
        return TasksService.getCourseTaskMemberships(this.courseId, { Terms: search, PageSize: 50, PageNumber: 1 })
          .then((r) => {
            const items = (r.data && r.data.items) || [];
            if (!items.length) setValue(null);
            else {
              setData(
                items.map((m) => ({
                  Value: m.tasksBankCourseId,
                  Text: `${m.task ? m.task.id : ""} - ${m.task ? m.task.name : ""}`,
                }))
              );
            }
          })
          .finally(() => loading(false));
      }
      return null;
    },
    save() {
      // FieldInlineSelect reduces the option to its Value, so model.id is the membership id.
      if (!this.isEdit && this.model.id == null) {
        Toast.open({ type: "is-warning", message: "יש לבחור משימה משויכת לקורס", duration: 4000 });
        return;
      }
      // The server rejects 0/negative with this exact message; blocking here saves a round trip.
      const duration = this.durationValue();
      if (duration != null && (!Number.isInteger(duration) || duration <= 0)) {
        Toast.open({
          type: "is-warning",
          message: "משך המשימה חייב להיות גדול מאפס. יש להשאיר את השדה ריק כדי להשתמש במשך מבנק המשימות.",
          duration: 6000,
        });
        return;
      }
      this.isLoading = true;
      const req = this.isEdit
        ? TasksService.updateStructuredPlanTasks(this.buildUpdateRow())
        : TasksService.addStructuredPlanTask(this.buildAddDto());

      req
        .then(() => {
          Toast.open({ type: "is-success", message: "הפעולה בוצעה!", duration: 4000 });
          if (this.onSuccess) this.onSuccess();
          this.$emit("saved");
          this.$emit("close");
        })
        .catch((error) => {
          // Surface the server's message (BadRequest -> { error }) when present.
          const serverMsg = error && error.response && error.response.data && error.response.data.error;
          Toast.open({
            type: "is-danger",
            message: serverMsg || "לא היה ניתן לבצע את הפעולה, יש לפנות לתמיכה הטכנית.",
            duration: 8000,
          });
        })
        .finally(() => { this.isLoading = false; });
    },
  },
};
</script>

<style scoped lang="scss">
.course-context {
  background: #eef6f5;
  border: 1px solid #b9e3de;
  border-radius: 4px;
  padding: 9px 12px;
  font-size: 13px;
  color: #0a7d74;
  margin-bottom: 18px;
  strong { color: #087a71; }
}
.time-window {
  display: flex;
  gap: 8px;
  width: 100%;
  button {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 10px 0;
    border: 1px solid #cbd6e2;
    border-radius: 6px;
    background: #fff;
    color: #43535f;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    .dot { width: 9px; height: 9px; border-radius: 50%; }
    &.on .dot { display: none; }
  }
}
.times {
  display: flex;
  gap: 14px;
  .field { flex: 1; }
}
.duration {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  .control { flex: 1; }
}
.duration-badge {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: #0a7d74;
  background: #eef6f5;
  border: 1px solid #b9e3de;
  border-radius: 10px;
  padding: 2px 9px;
  white-space: nowrap;
}
.duration-hint {
  margin-top: -8px;
  margin-bottom: 4px;
  font-size: 12px;
  color: #6b7b8c;
  line-height: 1.45;
}
</style>
